import React from 'react';
import { navigate } from 'hooks/useHookRouterCompat';
import { formatDateTime, formatPhone } from 'utils/formatters';
import styled from 'styled-components';
import Button from 'components/UI/Button';
import { filterDowJonesRelations } from 'utils/dowJones';
import DowJonesResult from 'data/DowJonesResult';
import { WarningRounded, Block } from '@mui/icons-material';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Height } from 'styles/themeHeights';
import { Main, Text } from 'styles/themeColors';
import { unique } from 'lib/filters';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import { ACH } from 'constants/paymentTypes';
import { customSort } from 'utils/grid';
import LineStepper from 'components/UI/LineStepper';
import FeatureFlag from 'data/FeatureFlag';
import { SHIPPING } from 'constants/deliveryModes';
import Order from 'data/Order';
import { CustomerVM } from 'data/Customer';
import {
  DOW_JONES_BLOCKED,
  DOW_JONES_REVIEWED_CLEAR,
  DOW_JONES_CLEAR,
  DOW_JONES_REVIEWED_BLOCKED,
} from 'constants/dowJonesStatuses';
import {
  CHECKING_CREDIT,
  ORDER_SENT_TO_OEM,
  READY,
  CANCELING,
  CANCELED,
  ON_HOLD,
  PROCESSING,
  MANUFACTURING,
  SPLIT_CONSIGNMENT,
  PACKING,
  SHIPPED_READY_FOR_PICKUP,
  READY_TO_SHIP,
  READY_FOR_PICKUP,
  DELIVERED_PICKED_UP,
  DELAYED,
  AWAITS_SHIPPING,
  AWAITS_SHIPPING_APPROVAL,
  AWAITS_EDIT_APPROVAL,
  DOW_JONES_FAILED,
  SHIPPED,
  PICKED_UP,
  RENT_IN_PROGRESS,
  RENT_RETURN_PENDING,
  RENT_COMPLETED,
  MODIFIED_BY_CUSTOMER,
  MODIFIED_BY_VENDOR,
  RENT_UPDATED,
  TICKET_IN_PROGRESS,
  CANCELATION_FAILED,
  CANCELATION_APPROVAL,
  CANCELLATION_IN_PROGRESS,
  SUBSCRIPTION_RENEWAL,
  SUBSCRIPTION_ACTIVE,
  AFFILIATE_PROGRAM_ACTIVE
} from 'constants/consignmentStates';
import Vendor from 'data/Vendor';
import { CONSIGNMENT_TYPES } from 'constants/order';
import { PAYMENT_STATUS } from 'constants/paymentStatus';
import { CREDIT_SOURCES, CREDIT_STATUS_MESSAGES, CREDIT_BUTTONS } from 'constants/credit';
import ConnectionStatusBadge from 'components/Company/ConnectionStatusBadge';

const Label = styled.label`
  color: red;
`;

const ErrorText = styled.span`
  color: ${Main.error} !important;
`;

const SecondaryText = styled.span`
  color: ${Text.secondary} !important;
`;

const ClickableContainer = styled(FlexboxContainer)`
  cursor: pointer;
`;

export const mapCompanyGridColumnsForSettings = (columns: any[], sortBy: any) => {
  return columns.map((column: any) => {
    // Same mapping logic but without filtering hidden columns
    const mappedColumn = { ...column };

    if (mappedColumn.field === 'vendor.vendorCompanyId') {
      mappedColumn.render = (params: any) => {
        const bu = params.businessUnit;
        const hasBuCredit = bu && bu.businessUnitCompanyId && bu.creditId;

        if (hasBuCredit) {
          return bu.creditSource === CREDIT_SOURCES.MDM
            ? bu.businessUnitCompanyId
            : <ConnectionStatusBadge
                status={CREDIT_STATUS_MESSAGES.TRACKED_BY_OFMP}
                backgroundColor={Main.success}
                color={Text.whitePrimary}
                margin='0px'
                width='160px'
              />;
        }

        return (
          <Button isGhost color={Main.accent}>
            {CREDIT_BUTTONS.ASSIGN}
          </Button>
        );
      };
    }

    if (mappedColumn.field === 'customerSince') {
      mappedColumn.render = (params: any) => formatDateTime(params.customerSince);
    }

    if (sortBy?.field === mappedColumn.field) {
      mappedColumn.defaultSort = sortBy?.defaultSort;
    } else {
      mappedColumn.defaultSort = undefined;
    }

    mappedColumn.customSort = customSort(sortBy);

    return mappedColumn;
  });
};

export const mapCompanyGridColumns = (columns: any[], sortBy: any) => {
  return columns
    .filter((column: any) => column.hidden !== true)
    .map((column: any) => {
      if (column.field === 'vendor.vendorCompanyId') {
        column.render = (params: any) => {
          const bu = params.businessUnit;
          const hasBuCredit = bu && bu.businessUnitCompanyId && bu.creditId;

          if (hasBuCredit) {
            return bu.creditSource === CREDIT_SOURCES.MDM
              ? bu.businessUnitCompanyId
              : <ConnectionStatusBadge
                  status={CREDIT_STATUS_MESSAGES.TRACKED_BY_OFMP}
                  backgroundColor={Main.success}
                  color={Text.whitePrimary}
                  margin='0px'
                  width='160px'
                />;
          }

          return (
            <Button isGhost color={Main.accent}>
              Assign
            </Button>
          );
        };
      }

      if (column.field === 'customerSince') {
        column.render = (params: any) => formatDateTime(params.customerSince);
      }

      if (sortBy?.field === column.field) {
        column.defaultSort = sortBy?.defaultSort;
      } else {
        column.defaultSort = undefined;
      }

      column.customSort = customSort(sortBy);

      return column;
    });
};

export const mapCustomerGridColumnsForSettings = (
  columns: any[],
  featureFlags: any,
  sortBy: any,
  isVendor: boolean,
  currentPage: number
) => {
  return columns
    .filter((column: any) =>
      filterDowJonesRelations(featureFlags, column.title)
    )
    .map((column: any) => {
      const mappedColumn = { ...column };

      if (mappedColumn.field === 'fullName') {
        mappedColumn.render = (params: any) => (
          <span style={{ fontWeight: 'bolder', textTransform: 'capitalize' }}>
            {params.fullName}
          </span>
        );
      }

      if (mappedColumn.field === 'phone') {
        mappedColumn.render = (params: any) => formatPhone(params.phone);
      }

      if (mappedColumn.field === 'createdDate') {
        mappedColumn.render = (params: any) => formatDateTime(params.createdDate);
      }

      if (mappedColumn.field === 'dowJones') {
        mappedColumn.render = (params: any) => {
          return params.dowJones?.map((dowJonesResult: DowJonesResult, index: number) => (
            <FlexboxContainer key={index} flexDirection="column" gap="4px">
              {[DOW_JONES_BLOCKED, DOW_JONES_REVIEWED_BLOCKED].includes(dowJonesResult.status) && (
                <FlexboxContainer alignItems="center" gap="4px">
                  <Block style={{ color: Main.error, fontSize: '16px' }} />
                  <ErrorText>Blocked</ErrorText>
                </FlexboxContainer>
              )}
            </FlexboxContainer>
          ));
        };
      }

      if (sortBy?.field === mappedColumn.field) {
        mappedColumn.defaultSort = sortBy?.defaultSort;
      } else {
        mappedColumn.defaultSort = undefined;
      }

      mappedColumn.customSort = customSort(sortBy);

      return mappedColumn;
    });
};

export const mapCustomerGridColumns = (
  columns: any[],
  featureFlags: any,
  sortBy: any,
  isVendor: boolean,
  currentPage: number
) => {
  return columns
    .filter((column: any) =>
      filterDowJonesRelations(featureFlags, column.title) && column.hidden !== true
    )
    .map((column: any) => {
      if (column.field === 'fullName') {
        column.render = (params: any) => (
          <span style={{ fontWeight: 'bolder', textTransform: 'capitalize' }}>
            {params.fullName}
          </span>
        );
      }

      if (column.field === 'phone') {
        column.render = (params: any) => formatPhone(params.phone);
      }

      if (column.field === 'createdDate') {
        column.render = (params: any) => formatDateTime(params.createdDate);
      }

      if (column.field === 'dowJonesStatus') {
        column.render = (params: any) =>
          getStatus(
            params.dowJonesStatus,
            params.dowJonesTimestamp,
            params.id,
            isVendor,
            currentPage
          );
      }

      if (sortBy?.field === column.field) {
        column.defaultSort = sortBy?.defaultSort;
      } else {
        column.defaultSort = undefined;
      }

      column.customSort = customSort(sortBy);

      return column;
    });
};

export const mapCustomerData = (customers: CustomerVM[]) => {
  return customers.map((customer) => {
    let companyName = '';
    let displayName = '';
    let companyNameList = customer.companies.map(
      (company) => company.companyName
    );
    let displayNameList = customer.companies.map(
      (company) => company.displayName
    );
    companyNameList.filter(unique);
    displayNameList.filter(unique);

    if (companyNameList.length > 0) {
      displayName = companyNameList.slice(0, 2).join(' | ');
    }

    if (displayNameList.length > 0) {
      companyName = displayNameList.slice(0, 2).join(' | ');
    }

    if (companyNameList.length > 2) {
      displayName += ` | +${companyNameList.length - 2} more...`;
    }

    if (displayNameList.length > 2) {
      companyName += ` | +${displayNameList.length - 2} more...`;
    }

    const dowJonesFailedResult = customer.dowJones.find(
      (dowJones: DowJonesResult) => dowJones.status === DOW_JONES_BLOCKED
    );
    const dowJonesStatus = dowJonesFailedResult
      ? dowJonesFailedResult.status
      : customer.dowJones.length > 0
        ? customer.dowJones[0].status
        : '';
    const dowJonesTimestamp = dowJonesFailedResult
      ? dowJonesFailedResult.dowJonesTimeStamp
      : '';

    return {
      ...customer,
      companyName,
      displayName,
      dowJonesStatus,
      dowJonesTimestamp,
    };
  });
};

export const mapOrderGridColumns = (
  columns: any[],
  isVendorUser: boolean,
  featureFlags: any,
  sortBy: any,
  selectedVendor?: Vendor
) => {
  return columns.map((column: any, index: number) => {
    if (column.field === 'orderId') {
      column.render = (params: any) => (
        <div style={{ minWidth: '150px', width: '150px' }}>{params.orderId}</div>
      );
    }

    if (column.field === 'created') {
      column.render = (params: any) => formatDateTime(params.created);
    }

    if (column.field === 'paymentType') {
      column.render = (params: any) => {
        return params.paymentType === ACH
          ? ACH
          : params.paymentType
            ? params.paymentType.charAt(0).toUpperCase() +
            params.paymentType.slice(1).toLowerCase().replace(/_/, ' ')
            : '';
      };
    }

    if (sortBy?.field === column.field) {
      column.defaultSort = sortBy?.defaultSort;
    } else {
      column.defaultSort = undefined;
    }

    if (column.field === 'status') {
      column.render = (params: any) => {
        const dowJonesStatus = params.dowJones ? params.dowJones.status : '';
        const deliveryTypes = params.consignments
          .map(
            (consignment: any) => consignment.consignmentEntries[0].deliveryMode
          )
          .filter(unique);



        // Get consignment types from all consignments
        const consignmentTypes = params.consignments
          .map((consignment: any) => consignment.consignmentType)
          .filter(unique);

        const consignmentType = consignmentTypes.length === 1
          ? consignmentTypes[0]
          : consignmentTypes.includes(CONSIGNMENT_TYPES.RENT)
            ? CONSIGNMENT_TYPES.RENT
            : CONSIGNMENT_TYPES.PURCHASE;

        const hasPaymentStatusFailed = params.consignments.some(
          (consignment: any) =>
            consignment.paymentStatus === 'FAILED'
        );

        const isRentBeingCharged = params.consignments.some(
          (consignment: any) =>
            consignment.state === RENT_COMPLETED &&
            consignment.paymentStatus === PAYMENT_STATUS.NOT_PAID
        );

        const { displayState, currentStep, stepsColor } = getSteps(
          params.state,
          deliveryTypes,
          isVendorUser,
          dowJonesStatus,
          featureFlags,
          consignmentType,
          selectedVendor,
          false,
          hasPaymentStatusFailed,
          isRentBeingCharged
        );

        return (
          <>
            <div>{displayState}</div>
            <LineStepper
              numberOfSteps={5}
              currentStep={currentStep}
              stepperWidth={90}
              stepsColor={stepsColor}
            />
          </>
        );
      };
    }

    column.customSort = customSort(sortBy);

    return column;
  });
};

export const mapOrderData = (
  orders: Order[],
  isVendorUser: boolean,
  featureFlags: any,
  selectedVendor?: Vendor
) => {
  return orders.map((order) => {
    if (order.consignments.some(cons => cons.consignmentEntries.length === 0)) {
      return null;
    }

    const updatedByList = order.consignments.every(
      (consignment) => consignment.updatedBy === order.consignments[0].updatedBy
    );

    const statesAreEqual = order.consignments.every(
      (consignment) => consignment.state === order.consignments[0].state
    );
    const warehousesAreEqual = order.consignments.every(
      (consignment) =>
        consignment.warehouse.name === order.consignments[0].warehouse.name
    );
    const deliveryTypesAreEqual = order.consignments.every(
      (consignment) =>
        consignment.consignmentEntries[0].deliveryMode ===
        order.consignments[0].consignmentEntries[0].deliveryMode
    );
    const vendorsAreEqual = order.consignments.every(
      (consignment) => consignment.vendorId === order.consignments[0].vendorId
    );
    const businessUnitsAreEqual = order.consignments.every(
      (consignment) => {
        const bu1 = consignment.businessUnit?.id || consignment.businessUnitId;
        const bu2 = order.consignments[0].businessUnit?.id || order.consignments[0].businessUnitId;
        return bu1 === bu2;
      }
    );

    order.state = statesAreEqual
      ? order.consignments[0].state
      : SPLIT_CONSIGNMENT;
    order.warehouse = warehousesAreEqual
      ? order.consignments[0].warehouse.name
      : 'Multiple';
    order.vendor = vendorsAreEqual
      ? order.consignments[0].vendor
        ? order.consignments[0].vendor.name
        : ''
      : 'Multiple';
    order.businessUnit = businessUnitsAreEqual
      ? order.consignments[0].businessUnit?.name || ''
      : 'Multiple';
    order.deliveryType = deliveryTypesAreEqual
      ? order.consignments[0].consignmentEntries[0].deliveryMode === SHIPPING
        ? 'Delivery'
        : 'Pickup'
      : 'Pickup / Delivery';

    order.updatedBy = updatedByList
      ? order.consignments[0].updatedBy
        ? order.consignments[0].updatedBy
        : ''
      : 'Multiple';
    order.customer.fullName =
      order.customer.firstName + ' ' + order.customer.lastName;

    const dowJonesStatus = order.dowJones ? order.dowJones.status : '';
    const deliveryTypes = order.consignments
      .map((consignment: any) => consignment.consignmentEntries[0].deliveryMode)
      .filter(unique);

    const hasPaymentStatusFailed = order.consignments.some(
      (consignment) =>
        consignment.paymentStatus === 'FAILED'
    );

    const isRentBeingCharged = order.consignments.some(
      (consignment: any) =>
        consignment.state === RENT_COMPLETED &&
        consignment.paymentStatus === PAYMENT_STATUS.NOT_PAID
    );

    const { displayState } = getSteps(
      order.state,
      deliveryTypes,
      isVendorUser,
      dowJonesStatus,
      featureFlags,
      order.consignments[0].consignmentType,
      selectedVendor,
      false,
      hasPaymentStatusFailed,
      isRentBeingCharged
    );
    order.status = displayState;

    return order;
  }).filter((order): order is Order => order !== null);
};

const getStatus = (
  dowJonesStatus: any,
  dowJonesFailedDate: string,
  customerId: number,
  isVendor: boolean,
  currentPage: number
) => {
  let displayStatus = dowJonesStatus;

  switch (dowJonesStatus) {
    case DOW_JONES_REVIEWED_CLEAR:
      displayStatus = (
        <span className="base">
          {isVendor ? 'Compliant' : 'Cleared manually'}
        </span>
      );
      break;
    case DOW_JONES_CLEAR:
      displayStatus = (
        <span className="base">{isVendor ? 'Compliant' : 'Clear'}</span>
      );
      break;
    case DOW_JONES_BLOCKED:
      displayStatus = isVendor ? (
        <SecondaryText className="base-italic">Under Review</SecondaryText>
      ) : (
        <ClickableContainer
          justifyContent="flex-start"
          bgColor={Main.warning}
          borderRadius={BorderRadius.m}
          padding={Space.spHalf}
          minWidth="12%"
          onClick={() =>
            navigate(`/customers/customer-details/${customerId}`, false, {
              customerPage: currentPage,
              activeTab: 1,
            })
          }
        >
          <WarningRounded
            style={{
              height: Height.xsPlus,
              width: '20px',
              marginRight: Space.spHalf,
            }}
          />
          <FlexboxContainer flexDirection="column" alignItems="flex-start">
            <span className="base-strong">Requires review</span>
            <span className="base">
              Failed at {formatDateTime(dowJonesFailedDate, true)}
            </span>
          </FlexboxContainer>
        </ClickableContainer>
      );
      break;
    case DOW_JONES_REVIEWED_BLOCKED:
      displayStatus = (
        <FlexboxContainer
          justifyContent="flex-start"
          alignItems="flex-start"
          padding="4px"
        >
          <Block
            style={{ color: Main.error, height: Height.xsPlus, width: '16px' }}
          />
          <ErrorText className="base">Blocked manually</ErrorText>
        </FlexboxContainer>
      );
      break;
    default:
      displayStatus = '';
  }

  return displayStatus;
};

export const getSteps = (
  consignmentState: string,
  deliveryTypes: string[],
  isVendor: boolean,
  dowJonesStatus: string,
  featureFlags: FeatureFlag,
  consignmentType: string = 'PURCHASE',
  selectedVendor?: Vendor,
  isInnerItem: boolean = false,
  hasPaymentStatusFailed: boolean = false,
  isRentBeingCharged: boolean = false
) => {
  let displayState = consignmentState;
  let currentStep = 0;
  let stepsColor = Main.success;
  let orderDetailsState = null;

  const isDowJonesBlocked =
    (dowJonesStatus === DOW_JONES_BLOCKED ||
      consignmentState === DOW_JONES_FAILED) &&
    featureFlags.dowJones &&
    Boolean(featureFlags.dowJones.enabled);
  if (isDowJonesBlocked) {
    if (isVendor || selectedVendor) {
      displayState = 'Compliance Check';
      currentStep = 2;
    } else if (isInnerItem) {
      displayState = 'Compliance Fail';
      currentStep = 2;
      stepsColor = Main.error;
    } else {
      displayState = 'Dow Jones Failed';
      currentStep = 2;
      stepsColor = Main.error;
    }

    return { displayState, currentStep, stepsColor };
  }

  if (hasPaymentStatusFailed) {
    displayState = 'Payment Failed';
    currentStep = 5;
    stepsColor = Main.error;
    return { displayState, currentStep, stepsColor };
  }


  if (isRentBeingCharged) {
    displayState = 'Charging';
    currentStep = 5;
    stepsColor = Main.warning;
    return { displayState, currentStep, stepsColor };
  }

  switch (consignmentState) {
    case CHECKING_CREDIT:
      displayState = 'Checking Credit';
      currentStep = 2;
      break;
    case READY:
    case ORDER_SENT_TO_OEM:
      if (isVendor) {
        displayState = isDowJonesBlocked ? 'Compliance Check' : 'New';
        currentStep = 1;
      } else {
        displayState = isDowJonesBlocked ? 'Compliance Fail' : 'New';
        currentStep = 1;
        stepsColor = isDowJonesBlocked ? Main.error : Main.success;
      }
      break;
    case DOW_JONES_FAILED:
      if (isVendor) {
        displayState = 'Compliance Check';
        currentStep = 2;
      } else {
        displayState = 'Compliance Fail';
        currentStep = 2;
        stepsColor = Main.error;
      }
      break;
    case AWAITS_SHIPPING:
      displayState = 'Shipping cost required';
      currentStep = 1;
      stepsColor = Main.warning;
      break;
    case AWAITS_SHIPPING_APPROVAL:
      displayState = 'Shipping cost approval';
      currentStep = 1;
      stepsColor = Main.warning;
      break;
    case AWAITS_EDIT_APPROVAL:
      displayState = 'Approval pending';
      currentStep = 1;
      stepsColor = Main.warning;
      break;
    case CANCELED:
      displayState = 'Canceled';
      currentStep = 5;
      stepsColor = Main.error;
      break;
    case CANCELING:
      displayState = isVendor ? 'Canceled' : 'Canceling';
      currentStep = 5;
      stepsColor = Main.error;
      break;
    case ON_HOLD:
      displayState = 'On Hold';
      currentStep = 1;
      stepsColor = Main.error;
      break;
    case PROCESSING:
      displayState = 'Processing';
      currentStep = 2;
      break;
    case SPLIT_CONSIGNMENT:
      displayState = 'Split Order';
      orderDetailsState = 'Splitting';
      currentStep = 2;
      stepsColor = Main.warning;
      break;
    case MANUFACTURING:
      displayState = 'Manufacturing';
      currentStep = 2;
      break;
    case PACKING:
      displayState = 'Preparing';
      currentStep = 2;
      break;
    case SHIPPED_READY_FOR_PICKUP:
      displayState =
        deliveryTypes.length > 1
          ? 'Shipped / Ready for Pickup'
          : deliveryTypes[0] === SHIPPING
            ? 'Ready to ship'
            : 'Ready for Pickup';
      currentStep = 3;
      break;
    case READY_TO_SHIP:
      displayState = 'Ready to ship';
      currentStep = 3;
      break;
    case READY_FOR_PICKUP:
      displayState = 'Ready for Pickup';
      currentStep = 3;
      break;
    case DELIVERED_PICKED_UP:
      displayState =
        deliveryTypes.length > 1
          ? 'Delivered / Picked up'
          : deliveryTypes[0] === SHIPPING
            ? 'Shipped'
            : 'Picked up';
      currentStep = 3;
      break;
    case SHIPPED:
      displayState = 'Shipped';
      currentStep = consignmentType === CONSIGNMENT_TYPES.RENT ? 3 : 5;
      break;
    case PICKED_UP:
      displayState = 'Picked up';
      currentStep = consignmentType === CONSIGNMENT_TYPES.RENT ? 3 : 5;
      break;
    case RENT_IN_PROGRESS:
      displayState = 'Rental in progress';
      currentStep = 3;
      break;
    case TICKET_IN_PROGRESS:
      displayState = 'Ticket in progress';
      currentStep = 3;
      stepsColor = Main.error;
      break;
    case RENT_RETURN_PENDING:
      displayState = 'Rental return pending';
      currentStep = 4;
      break;
    case RENT_COMPLETED:
      displayState = 'Rental completed';
      currentStep = 5;
      break;
    case DELAYED:
      displayState = 'Delayed';
      currentStep = 3;
      stepsColor = Main.warning;
      break;
    case MODIFIED_BY_CUSTOMER:
      displayState = 'Rent modified by customer';
      currentStep = 3;
      stepsColor = Main.warning;
      break;
    case MODIFIED_BY_VENDOR:
      displayState = 'Rent modified by vendor';
      currentStep = 3;
      stepsColor = Main.warning;
      break;
    case RENT_UPDATED:
      displayState = 'Rent Updated';
      currentStep = 3;
      stepsColor = Main.warning;
      break;
    case CANCELATION_FAILED:
      displayState = 'Cancellation Failed';
      currentStep = 4;
      stepsColor = Main.error;
      break;
    case CANCELATION_APPROVAL:
      displayState = 'Cancellation Approval';
      currentStep = 4;
      stepsColor = Main.warning;
      break;
    case CANCELLATION_IN_PROGRESS:
      displayState = 'Cancellation in progress';
      currentStep = 4;
      stepsColor = Main.warning;
      break;
    case SUBSCRIPTION_ACTIVE:
      displayState = 'Subscription active';
      currentStep = 3;
      stepsColor = Main.success;
      break;
    case SUBSCRIPTION_RENEWAL:
      displayState = 'Subscription renewal';
      currentStep = 4;
      stepsColor = Main.warning;
      break;
    case AFFILIATE_PROGRAM_ACTIVE:
      displayState = 'Affiliate program active';
      currentStep = 3;
      stepsColor = Main.success;
      break;
    default:
      currentStep = 1;
      stepsColor = Main.success;
  }

  return { displayState, currentStep, stepsColor, orderDetailsState };
};

export const mapCompanyDocumentsGridColumns = (columns: any[], sortBy: any) => {
  return columns.map((column: any) => {
    if (column.field === 'creationTime') {
      column.render = (params: any) => formatDateTime(params.creationTime);
    }

    if (column.field === 'category.name') {
      column.render = (params: any) => {
        const value =
          params.category && params.category.name ? params.category.name : '';

        return value;
      };
    }

    if (column.field === 'expirationDate') {
      column.render = (params: any) => {
        const currentDate = new Date();
        const value =
          new Date(params.expirationDate).setUTCHours(0, 0, 0, 0) >
            new Date(currentDate).setUTCHours(0, 0, 0, 0) ? (
            formatDateTime(params.expirationDate)
          ) : (
            <Label>Expired</Label>
          );
        return value;
      };
    }

    if (sortBy?.field === column.field) {
      column.defaultSort = sortBy?.defaultSort;
    } else {
      column.defaultSort = undefined;
    }

    column.customSort = customSort(sortBy);

    return column;
  });
};
